<template>
  <v-navigation-drawer
    id="appDrawer"
    :mini-variant.sync="mini"
    fixed
    :dark="$vuetify.dark"
    :right="$vuetify.rtl"
    app
    v-model="drawer"
    width="260"
  >
    <v-toolbar color="primary darken-1" dark>
      <v-toolbar-title class="ms-0 ps-3">
        <span class="hidden-sm-and-down">{{ $t("app.name") }}</span>
      </v-toolbar-title>
    </v-toolbar>
    <vue-perfect-scrollbar
      class="drawer-menu--scroll"
      style="padding-bottom: 30px"
      :settings="scrollSettings"
    >
      <v-list dense expand>
        <template v-for="(item, i) in menus">
          <!--group with subitems-->
          <v-list-group
            v-if="item.items"
            :key="item.name"
            :group="item.group"
            :prepend-icon="item.icon"
            no-action="no-action"
          >
            <v-list-tile slot="activator" ripple="ripple">
              <v-list-tile-content>
                <v-list-tile-title>{{
                  item.i18nKey && $te("menu." + item.i18nKey)
                    ? $t("menu." + item.i18nKey)
                    : item.title
                }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <template v-for="(subItem, i) in item.items">
              <!--sub group-->
              <v-list-group
                v-if="subItem.items"
                :key="subItem.name"
                :group="subItem.group"
                sub-group="sub-group"
              >
                <v-list-tile slot="activator" ripple="ripple">
                  <v-list-tile-content>
                    <v-list-tile-title>{{
                      subItem.i18nKey && $te("menu." + subItem.i18nKey)
                        ? $t("menu." + subItem.i18nKey)
                        : subItem.title
                    }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile
                  v-for="(grand, i) in subItem.children"
                  :key="i"
                  :to="genChildTarget(item, grand)"
                  :href="grand.href"
                  @click="drawer = false"
                  @ripple="ripple"
                >
                  <v-list-tile-content>
                    <v-list-tile-title>{{
                      grand.i18nKey && $te("menu." + grand.i18nKey)
                        ? $t("menu." + grand.i18nKey)
                        : grand.title
                    }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list-group>
              <!--child item-->
              <v-list-tile
                v-else
                :key="i"
                :to="genChildTarget(item, subItem)"
                :href="subItem.href"
                @click="drawer = false"
                :disabled="subItem.disabled"
                :target="subItem.target"
                ripple="ripple"
              >
                <v-list-tile-content>
                  <v-list-tile-title
                    ><span>{{
                      subItem.i18nKey && $te("menu." + subItem.i18nKey)
                        ? $t("menu." + subItem.i18nKey)
                        : subItem.title
                    }}</span></v-list-tile-title
                  >
                </v-list-tile-content>
                <v-list-tile-action v-if="subItem.action">
                  <v-icon :class="[subItem.actionClass || 'success--text']"
                    >{{ subItem.action }}
                  </v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </template>
          </v-list-group>
          <v-subheader v-else-if="item.header" :key="i">{{
            item.i18nKey && $te("menu.headers." + item.i18nKey)
              ? $t("menu.headers." + item.i18nKey)
              : item.header
          }}</v-subheader>
          <v-divider v-else-if="item.divider" :key="i"></v-divider>
          <!--top-level link-->
          <v-list-tile
            v-else
            :to="!item.href ? { name: item.name } : null"
            ripple="ripple"
            @click="drawer = false"
            :disabled="item.disabled"
            rel="noopener"
            :key="item.name"
          >
            <v-list-tile-action v-if="item.icon">
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{
                item.i18nKey && $te("menu." + item.i18nKey)
                  ? $t("menu." + item.i18nKey)
                  : item.title
              }}</v-list-tile-title>
            </v-list-tile-content>
            <v-list-tile-action v-if="item.subAction">
              <v-icon class="success--text">{{ item.subAction }}</v-icon>
            </v-list-tile-action>
          </v-list-tile>
        </template>
      </v-list>
    </vue-perfect-scrollbar>
  </v-navigation-drawer>
</template>
<script>
import menu from "@/constants/menu";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

export default {
  name: "app-drawer",
  components: {
    VuePerfectScrollbar,
  },
  props: {
    expanded: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    mini: false,
    drawer: false,
    scrollSettings: {
      maxScrollbarLength: 160,
    },
  }),
  computed: {
    menus() {
      // First pass: filter items based on permissions
      const filteredMenu = menu.filter((item) => {
        if (item.items) {
          const items = item.items.filter(
            ({ permission }) => !permission || this.hasPermission(permission)
          );
          if (items.length === 0) return false;
          else {
            item.items = items;
            return true;
          }
        }
        // Keep headers and dividers for now, we'll filter them in second pass
        if (item.header || item.divider) return true;
        return !item.permission || this.hasPermission(item.permission);
      });

      // Second pass: remove headers that don't have visible items after them
      const result = [];
      for (let i = 0; i < filteredMenu.length; i++) {
        const item = filteredMenu[i];
        
        // If it's a header, check if there are any visible items after it
        if (item.header) {
          let hasVisibleItems = false;
          // Look ahead to find next header/divider or end of array
          for (let j = i + 1; j < filteredMenu.length; j++) {
            const nextItem = filteredMenu[j];
            // If we hit another header or divider, stop
            if (nextItem.header || nextItem.divider) break;
            // If we found a visible item (not a header/divider), keep this header
            hasVisibleItems = true;
            break;
          }
          if (hasVisibleItems) {
            result.push(item);
          }
          // Otherwise, skip this header
        } else {
          result.push(item);
        }
      }

      return result;
    },
    computeGroupActive() {
      return true;
    },
    computeLogo() {
      return "/static/m.png";
    },

    sideToolbarColor() {
      return this.$vuetify.options.extra.sideNav;
    },
  },
  created() {
    window.getApp.$on("APP_DRAWER_TOGGLED", () => {
      this.drawer = !this.drawer;
    });
  },
  methods: {
    genChildTarget(item, subItem) {
      if (subItem.href) return;
      if (subItem.component) {
        return {
          name: subItem.component,
        };
      }
      if (subItem.name) {
        return {
          name: subItem.name,
        };
      }
      return { name: `${item.group}/${subItem.name}` };
    },
  },
};
</script>

<style lang="stylus">
// @import '../../node_modules/vuetify/src/stylus/settings/_elevations.styl';

#appDrawer
    overflow: hidden

    .drawer-menu--scroll
        height: calc(100vh - 48px)
        overflow: auto
</style>
